// ────────────────────────────────────────────────────────────────────────────
// Driver messages — one place for every driver's query, on every screen.
//
// A round icon pinned to the top-right corner. Its badge is the unread driver
// messages on jobs YOU sent (amber) — whoever sent or updated a job is who the
// driver is most likely asking — with a quiet dot when there are unread ones on
// colleagues' jobs. Clicking opens the conversations (Mine / All) and a thread
// can be read and answered right there, or opened on its order.
//
// "Read" is shared by the office: opening a thread marks the driver's messages
// read for everyone, so two planners don't answer the same query.
//
// Only with TMS Mobile (the `mobile` capability).
// ────────────────────────────────────────────────────────────────────────────
import { useAuth0 } from '@auth0/auth0-react';
import { usePermissions } from '@my/directory-api/hooks';
import type { DriverInboxThread } from '@my/tms-api';
import { useDriverInbox } from '@my/tms-api/hooks';
import { ErrorBanner, Loading, cn, showToast } from '@my/ui';
import { ArrowLeft, ExternalLink, MessageSquare, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { Thread } from '@/features/orders/components/DispatchPanel';

const STATE_LABEL: Record<string, string> = {
  Sent: 'Not accepted yet',
  Accepted: 'Accepted',
  Queried: 'Queried',
  Completed: 'Completed',
  Withdrawn: 'Withdrawn',
  Superseded: 'Updated',
};

const orderOf = (t: DriverInboxThread) => t.orderNumber ?? 'order';

const ago = (iso: string) => {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

export function DriverInbox() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const { can } = usePermissions();
  const enabled = can('mobile') && !!orgId;

  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [selected, setSelected] = useState<DriverInboxThread | null>(null);
  const navigate = useNavigate();

  // The badge always reads "mine"; the panel shows the chosen scope.
  const mine = useDriverInbox(orgId, 'mine', enabled);
  const shown = useDriverInbox(orgId, scope, enabled && open && scope === 'all');
  const data = scope === 'mine' ? mine.data : shown.data;
  const loading = scope === 'mine' ? mine.isPending : shown.isPending;
  const error = scope === 'mine' ? mine.error : shown.error;

  // Keep page headers clear of the icon while it is shown.
  useEffect(() => {
    if (!enabled) return;
    document.documentElement.style.setProperty('--page-header-inset', '3.5rem');
    return () => {
      document.documentElement.style.removeProperty('--page-header-inset');
    };
  }, [enabled]);

  // A heads-up when a new message lands on one of your jobs — not on first load.
  const lastUnread = useRef<number | null>(null);
  useEffect(() => {
    const now = mine.data?.mineUnread;
    if (now === undefined) return;
    const before = lastUnread.current;
    lastUnread.current = now;
    if (before === null || now <= before || open) return;
    const newest = mine.data?.threads.find((t) => t.unread > 0 && t.lastMessage?.fromRole === 'Driver');
    showToast({
      type: 'info',
      durationMs: 8000,
      message: newest
        ? `${newest.recipientName} on ${orderOf(newest)}: “${newest.lastMessage!.text}”`
        : 'New message from a driver.',
    });
  }, [mine.data, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && (selected ? setSelected(null) : setOpen(false));
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, selected]);

  if (!enabled) return null;

  const mineUnread = mine.data?.mineUnread ?? 0;
  const othersUnread = Math.max(0, (mine.data?.allUnread ?? 0) - mineUnread);

  const openOrder = (t: DriverInboxThread) => {
    setOpen(false);
    setSelected(null);
    navigate(`/orders/${t.orderId}?messages=1`);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Driver messages"
        title={
          mineUnread || othersUnread
            ? `Driver messages — ${mineUnread} new on your jobs${othersUnread ? `, ${othersUnread} on others'` : ''}`
            : 'Driver messages'
        }
        className="fixed right-4 top-3 z-30 flex h-10 w-10 items-center justify-center rounded-full border border-slate-300 bg-white text-slate-600 shadow-sm hover:bg-slate-50 hover:text-slate-800"
      >
        <MessageSquare className="h-5 w-5" />
        {mineUnread > 0 ? (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white ring-2 ring-white">
            {mineUnread}
          </span>
        ) : (
          othersUnread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-slate-400 ring-2 ring-white" />
          )
        )}
      </button>

      <div
        aria-hidden
        onClick={() => setOpen(false)}
        className={cn(
          'fixed inset-0 z-40 bg-slate-900/20 transition-opacity',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Driver messages"
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex w-[30rem] max-w-[96%] flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : 'pointer-events-none translate-x-full',
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div className="flex min-w-0 items-center gap-2">
            {selected && (
              <button
                type="button"
                onClick={() => setSelected(null)}
                aria-label="Back to the conversations"
                className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <div className="min-w-0">
              <h2 className="truncate text-sm font-semibold text-slate-900">
                {selected ? `${selected.recipientName} · ${orderOf(selected)}` : 'Driver messages'}
              </h2>
              <p className="text-[11px] text-slate-500">
                {selected
                  ? (selected.customerName ?? '')
                  : 'Queries and replies from drivers on myDriver. Opening one marks it read for the whole office.'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {selected ? (
          <div className="flex-1 overflow-y-auto p-4">
            <button
              type="button"
              onClick={() => openOrder(selected)}
              className="mb-2 inline-flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Open order {orderOf(selected)}
            </button>
            <Thread
              organisationId={orgId}
              chainId={selected.chainId}
              orderId={selected.orderId}
              canWrite={['Sent', 'Accepted', 'Queried'].includes(selected.status)}
            />
          </div>
        ) : (
          <>
            <div role="tablist" className="flex gap-1 border-b border-slate-200 px-4 pt-2">
              {(
                [
                  ['mine', `Mine${mineUnread ? ` (${mineUnread})` : ''}`],
                  ['all', `All${mine.data?.allUnread ? ` (${mine.data.allUnread})` : ''}`],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={scope === id}
                  onClick={() => setScope(id)}
                  className={cn(
                    '-mb-px border-b-2 px-3 py-1.5 text-xs font-medium',
                    scope === id ? 'border-sky-600 text-sky-700' : 'border-transparent text-slate-500 hover:text-slate-800',
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading && open && (
                <div className="p-4">
                  <Loading label="Loading messages…" />
                </div>
              )}
              {error && (
                <div className="p-4">
                  <ErrorBanner title="Couldn't load driver messages" message={error.message} />
                </div>
              )}
              {data && data.threads.length === 0 && (
                <p className="px-4 py-8 text-center text-xs text-slate-500">
                  {scope === 'mine'
                    ? 'No messages on the jobs you sent.'
                    : 'No driver messages in this organisation yet.'}
                </p>
              )}
              <ul className="divide-y divide-slate-100">
                {data?.threads.map((t) => (
                  <li key={t.chainId}>
                    <button
                      type="button"
                      onClick={() => setSelected(t)}
                      className={cn('block w-full px-4 py-2.5 text-left hover:bg-slate-50', t.unread > 0 && 'bg-amber-50/40')}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-semibold text-slate-900">
                          {t.recipientName} · {orderOf(t)}
                        </span>
                        <span className="flex shrink-0 items-center gap-1.5">
                          {t.unread > 0 && (
                            <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-semibold text-white">
                              {t.unread} new
                            </span>
                          )}
                          {t.lastMessage && <span className="text-[10px] text-slate-400">{ago(t.lastMessage.sentUtc)}</span>}
                        </span>
                      </div>
                      <p className="truncate text-[11px] text-slate-500">
                        {t.customerName ?? ''}
                        {t.customerName ? ' · ' : ''}
                        {STATE_LABEL[t.status] ?? t.status}
                        {!t.isMine && ` · sent by ${t.sentByName}`}
                      </p>
                      {t.lastMessage && (
                        <p className={cn('mt-0.5 truncate text-xs', t.unread > 0 ? 'text-slate-900' : 'text-slate-600')}>
                          {t.lastMessage.fromRole === 'Planner' ? 'You: ' : ''}
                          {t.lastMessage.text}
                        </p>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </aside>
    </>
  );
}
